import { partials } from "@shopify/partial-rendering";

// <s-product-recommendations> lazily fetches related/complementary products and swaps them
// into its {% partial %} region using the base theme's partial-rendering stack — the same
// @shopify/partial-rendering + view-transition that <s-live-form> (filters) is built on.
// One component per file, framework-free.
//
// When the block was already rendered in the recommendations context (data-performed="true"),
// the server markup is kept and no fetch happens. Otherwise, once the block nears the
// viewport, it reads Shopify's recommendations JSON and swaps rendered cards into the partial region.
class ProductRecommendations extends HTMLElement {
  #observer = null;
  #controller = null;

  connectedCallback() {
    if (this.dataset.performed === "true") {
      this.#syncVisibility();
      return;
    }
    if (!this.dataset.url || !this.dataset.productId || !this.dataset.partial) return;

    this.#observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0]?.isIntersecting) return;
        observer.disconnect();
        this.#load();
      },
      { rootMargin: "0px 0px 400px 0px" },
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#controller?.abort();
  }

  async #load() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const params = new URL(this.dataset.url, location.origin).searchParams;
    const limit = Math.max(1, parseInt(params.get('limit'), 10) || 4);
    // Shopify's storefront /recommendations/products route only renders HTML through a section_id;
    // this theme has no sections and the route answers 422 to ?partials=. So read the native
    // Product Recommendations API as JSON (real products, same intent/limit) and render each card
    // through the theme's own card markup (templates/product.rec-card.liquid, ?view=rec-card).
    const jsonUrl = new URL(this.dataset.url.split('?')[0] + '.json', location.origin);
    jsonUrl.searchParams.set('product_id', this.dataset.productId);
    jsonUrl.searchParams.set('limit', String(limit));
    jsonUrl.searchParams.set('intent', params.get('intent') || this.dataset.intent || 'related');

    this.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(jsonUrl, { signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Recommendations failed: ${response.status}`);
      const { products = [] } = await response.json();
      const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
      const cards = await Promise.all(
        products
          .filter((product) => product && product.handle && String(product.id) !== String(this.dataset.productId))
          .slice(0, limit)
          .map(async (product) => {
            try {
              const res = await fetch(`${root}products/${encodeURIComponent(product.handle)}?view=rec-card`, { signal, headers: { Accept: 'text/html' } });
              if (!res.ok) return '';
              const html = (await res.text()).trim();
              return html.startsWith('<li') ? html : '';
            } catch (error) {
              if (error.name === 'AbortError') throw error;
              return '';
            }
          }),
      );
      const markup = cards.filter(Boolean).join('');
      const region = partials.getAll(this).find(({ name }) => name === this.dataset.partial);
      if (!markup || !region) {
        // Shopify returned no recommendations: hide the whole block, no skeletons, no error.
        this.hidden = true;
        return;
      }
      const template = document.createElement('template');
      template.innerHTML = `<ul role="list" class="grid grid-cols-2 lg:grid-cols-4 gap-x-3 gap-y-8 md:gap-x-5" data-rec-fade>${markup}</ul>`;
      // No document.startViewTransition here: it snapshots the whole page and cross-fades the old
      // snapshot over the live one, and when the cards land while the shopper is scrolling fast
      // (Chrome Android) the old snapshot is dragged over the description/"Completa tu look" (ghosting).
      // Swap in place over the same-size skeleton and fade only the new grid (opacity, no layout move).
      region.range.deleteContents();
      region.range.insertNode(template.content);
      this.dataset.performed = 'true';
      this.#syncVisibility();
    } catch (error) {
      // Fetch failed: collapse quietly instead of showing skeletons forever.
      if (error.name !== 'AbortError') this.hidden = true;
    } finally {
      this.removeAttribute('aria-busy');
      this.#controller = null;
    }
  }

  #syncVisibility() {
    const region = partials.getAll(this).find(({ name }) => name === this.dataset.partial);
    const content = region?.range.cloneContents();
    this.hidden = !content || (!content.firstElementChild && !content.textContent.trim());
  }
}

if (!customElements.get("s-product-recommendations")) {
  customElements.define("s-product-recommendations", ProductRecommendations);
}
